import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  Autocomplete,
  Box,
  Button,
  IconButton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { salesService, creditNoteService } from '../../services';

/**
 * Debit Note Details Component
 * 
 * @param {Object} props
 * @param {Object} props.debitNote - Debit note data
 * @param {Array} props.salesInvoices - List of sales invoices
 * @param {Object} props.selectedInvoice - Currently selected invoice
 * @param {Object} props.controlStyle - Style object for form controls
 * @param {Function} props.onDebitNoteChange - Function to handle debit note changes
 * @param {Function} props.onDateChange - Function to handle date changes
 * @param {Function} props.onInvoiceSelect - Function to handle invoice selection
 */
const DebitNoteDetails = ({
  debitNote,
  salesInvoices,
  selectedInvoice,
  controlStyle,
  onDebitNoteChange,
  onDateChange,
  onInvoiceSelect
}) => {
  const [isLoading, setIsLoading] = useState(false);
  
  // Fetch next debit note number when component mounts
  useEffect(() => {
    // Only fetch if we don't already have a debit note number
    if (!debitNote.debitNoteNumber) {
      fetchNextDebitNoteNumber();
    }
  }, []);

  // Fetch invoice details when an invoice is selected
  useEffect(() => {
    // Check if we have a selected invoice
    if (selectedInvoice) {
      console.log('DebitNoteDetails: Selected invoice changed:', selectedInvoice);
      console.log('DebitNoteDetails: Current debitNote state:', debitNote);
      
      // Check if we're dealing with API response format (master and details)
      if (selectedInvoice.data && selectedInvoice.data.master) {
        const { master } = selectedInvoice.data;
        console.log('DebitNoteDetails: Processing API response format with master data:', master);
        console.log('DebitNoteDetails: Master data fields:', Object.keys(master));
        
        // Update the originalInvoice field with data from the master object
        const updatedDebitNote = {
          ...debitNote,
          originalInvoice: {
            invoiceNo: master.invoiceNo,
            invoiceDate: master.invoiceDate,
            totalAmount: master.totalAmt || 0
          }
        };
        
        console.log('DebitNoteDetails: Updated debit note with API response data:', updatedDebitNote.originalInvoice);
        console.log('DebitNoteDetails: Full updated debit note:', updatedDebitNote);
        onDebitNoteChange(updatedDebitNote);
      } 
      // Handle standard invoice format
      else if (selectedInvoice.invoiceNo) {
        console.log('DebitNoteDetails: Processing standard invoice format');
        console.log('DebitNoteDetails: Invoice fields:', Object.keys(selectedInvoice));
        
        // Update the originalInvoice field with data from the invoice object
        const updatedDebitNote = {
          ...debitNote,
          originalInvoice: {
            invoiceNo: selectedInvoice.invoiceNo,
            invoiceDate: selectedInvoice.invoiceDate,
            totalAmount: selectedInvoice.totalAmt || selectedInvoice.total || 0
          }
        };
        
        console.log('DebitNoteDetails: Updated debit note with standard invoice data:', updatedDebitNote.originalInvoice);
        console.log('DebitNoteDetails: Full updated debit note:', updatedDebitNote);
        onDebitNoteChange(updatedDebitNote);
      }
    }
  }, [selectedInvoice, debitNote]);

  // Function to process invoice data that already has complete information
  const processInvoiceData = (invoiceData) => {
    try {
      console.log('DebitNoteDetails: Processing invoice data:', invoiceData);
      
      // Check if we're dealing with API response format
      if (invoiceData.success && invoiceData.data) {
        const { master, details } = invoiceData.data;
        
        if (master && details) {
          console.log('DebitNoteDetails: Processing API response format with master and details');
          
          // Create a complete invoice object with the data
          const completeInvoice = {
            invoiceNo: master.invoiceNo,
            invoiceDate: master.invoiceDate,
            customerName: master.customerName,
            customerCode: master.customerCode,
            vatNumber: master.vat,
            customerAddress: master.customerAddress,
            customerStreetName: master.customerStreetName,
            customerPostalCode: master.customerPostalCode,
            customerPhone: master.customerPhone,
            paymentStatus: master.paymentStatus,
            paidAmt: master.paidAmt,
            balanceAmt: master.balanceAmt,
            subTotal: master.subTotal,
            vatAmt: master.vatAmt,
            totalAmt: master.totalAmt,
            items: details.map(item => ({
              itemCode: item.itemCode,
              description: item.description,
              qty: item.qty,
              unitPrice: item.unitPrice,
              discountPerc: item.discountPerc,
              vat: item.vat,
              vatAmt: item.vatAmt,
              grandTotal: item.grandTotal
            }))
          };
          
          console.log('DebitNoteDetails: Created complete invoice object:', completeInvoice);
          onInvoiceSelect(completeInvoice);
          
          // Also update the debit note with customer information
          const updatedDebitNote = {
            ...debitNote,
            originalInvoice: {
              invoiceNo: master.invoiceNo,
              invoiceDate: master.invoiceDate,
              totalAmount: master.totalAmt || 0
            },
            customer: {
              name: master.customerName,
              code: master.customerCode,
              vatNumber: master.vat ? master.vat.toString() : '',
              streetName: master.customerAddress || master.customerStreetName || '',
              address: master.customerAddress || '',
              phone: master.customerPhone || '',
              postalCode: master.customerPostalCode || ''
            }
          };
          onDebitNoteChange(updatedDebitNote);
        } else {
          console.warn('Missing master or details data in invoice');
        }
      }
    } catch (error) {
      console.error('Error processing invoice data:', error);
    }
  };

  // Function to fetch invoice details from the API
  const fetchInvoiceDetails = async (invoiceNumber) => {
    try {
      // Use the salesService instead of direct axios call
      const response = await salesService.getSalesInvoiceByInvoiceNo(invoiceNumber);

      if (response && response.success) {
        const { master, details } = response.data;
        
        // Create a complete invoice object with the data from the API
        const completeInvoice = {
          ...selectedInvoice,
          invoiceNo: master.invoiceNo,
          invoiceDate: master.invoiceDate,
          customerName: master.customerName,
          customerCode: master.customerCode,
          vatNumber: master.vat,
          customerAddress: master.customerAddress,
          customerPhone: master.customerPhone,
          items: details.map(item => ({
            itemCode: item.itemCode,
            description: item.description,
            quantity: item.qty,
            price: item.unitPrice,
            discount: item.discountPerc,
            vatRate: item.vat,
            total: item.grandTotal
          }))
        };
        
        onInvoiceSelect(completeInvoice);
        
        // Also update the debit note with customer information
        const updatedDebitNote = {
          ...debitNote,
          customer: {
            name: master.customerName,
            code: master.customerCode,
            vatNumber: master.vat,
            streetName: master.customerAddress,
            phone: master.customerPhone
          }
        };
        onDebitNoteChange(updatedDebitNote);
      } else {
        console.warn('API request failed or returned no data:', response.data);
      }
    } catch (error) {
      console.error('Error fetching invoice details:', error);
    }
  };

  // Function to fetch the next debit note number from the API
  const fetchNextDebitNoteNumber = async () => {
    setIsLoading(true);
    try {
      // Use the creditNoteService instead of direct axios call
      // Note: You may need to create a debitNoteService or modify the endpoint
      const response = await creditNoteService.getNextCreditNoteNumber();

      if (response && response.success) {
        // Update the debit note with the new number, but preserve the customer data
        console.log('Fetched next debit note number:', response.nextInvoiceNumber);
        console.log('Current debitNote state before update:', debitNote);
        
        // Only update the debitNoteNumber field, preserve all other fields
        onDebitNoteChange(prev => ({
          ...prev,
          debitNoteNumber: response.nextInvoiceNumber
        }));
      }
    } catch (error) {
      console.error('Error fetching next debit note number:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle reason change
  const handleReasonChange = (e) => {
    onDebitNoteChange({ ...debitNote, reason: e.target.value });
  };

  // Handle ref invoice number change
  const handleRefInvoiceNumberChange = (e) => {
    onDebitNoteChange({ ...debitNote, originalInvoice: e.target.value });
  };

  // Function to open the SelectInvoiceDialog
  const handleOpenSelectInvoiceDialog = () => {
    if (onInvoiceSelect) {
      // Call the parent's openSelectInvoiceDialog function
      onInvoiceSelect('open');
    }
  };

  // Debug the structure of the debitNote object before rendering
  console.log('DebitNoteDetails: Rendering with debitNote:', debitNote);
  console.log('DebitNoteDetails: originalInvoice structure:', debitNote.originalInvoice);
  console.log('DebitNoteDetails: selectedInvoice:', selectedInvoice);
  
  // Ensure we have a valid originalInvoice object to prevent rendering errors
  const originalInvoice = debitNote.originalInvoice || {};
  
  // Log the specific fields we're trying to access
  console.log('DebitNoteDetails: Accessing invoiceNo:', originalInvoice.invoiceNo);
  console.log('DebitNoteDetails: Accessing invoiceDate:', originalInvoice.invoiceDate);
  
  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 0.5, 
        mb: 0.5, 
        borderRadius: '4px',
        boxShadow: 'none',
        border: 'none',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
        },
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5, fontSize: '0.875rem' }}>
        Debit Note Details
      </Typography>
      <Grid container spacing={0.5}>
        <Grid item xs={12} sm={6}>
          <Box sx={{ position: 'relative' }}>
            <TextField
              label="Ref Invoice Number"
              value={originalInvoice.invoiceNo || ''}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton 
                    size="small" 
                    onClick={handleOpenSelectInvoiceDialog}
                    sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}
                  >
                    <SearchIcon fontSize="small" />
                  </IconButton>
                )
              }}
              required
              fullWidth
              margin="dense"
              size="small"
              sx={controlStyle}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Debit Note Number"
            value={isLoading ? 'Loading...' : (debitNote.debitNoteNumber || '')}
            fullWidth
            margin="dense"
            size="small"
            InputProps={{
              readOnly: true,
            }}
            sx={controlStyle}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Ref Invoice Date"
              value={originalInvoice.invoiceDate ? new Date(originalInvoice.invoiceDate) : null}
              readOnly
              slotProps={{ 
                textField: { 
                  fullWidth: true, 
                  margin: "dense",
                  size: "small",
                  sx: controlStyle,
                  InputProps: { readOnly: true }
                } 
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Date"
              value={debitNote.date}
              readOnly
              slotProps={{ 
                textField: { 
                  fullWidth: true, 
                  margin: "dense",
                  size: "small",
                  sx: controlStyle,
                  InputProps: { readOnly: true }
                } 
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Reason for Debit Note"
            value={debitNote.reason}
            onChange={handleReasonChange}
            fullWidth
            multiline
            rows={2.5}
            margin="dense"
            size="small"
            required
            sx={{
              ...controlStyle,
              '& .MuiOutlinedInput-root': {
                minHeight: 90,
                borderRadius: 0
              }
            }}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default DebitNoteDetails;
